import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CTAButton } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
const logos = [{
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/dp2V9t4KBqtNRKwEZ16zWE/Premier-Roofing-Logo-800.webp',
  alt: 'Premier Roofing'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mWRbtmgL6tZDZ35pDz81aY/ChiprLogo__1_.png',
  alt: 'Chipr'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qPbuvohPjZaBcU1aJP17AB/GHL_Logo_512x512_px.png',
  alt: 'GoHighLevel'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2FvmcCdQfLDAms5XrgWX9z/Freedom_WISP.jpg',
  alt: 'Freedom WISP'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/84u9Gp2QPa7E6EJEpaZ3NU/ME.Typeform_Transparent.png',
  alt: 'Median Energy'
}];
export function AuthoritySection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.15
  });
  return <section className="relative w-full bg-[#1A2B4A] dot-texture py-24 px-6 overflow-hidden" data-id="element-76">
      <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-[#4ECDC4]/6 rounded-full blur-[100px] pointer-events-none" data-id="element-77" />

      <div ref={ref} className="max-w-5xl mx-auto relative z-10" data-id="element-78">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-14" data-id="element-79">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-80">
            Who We Are
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-81">
            Built From the Field. Not a Boardroom.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-16" data-id="element-82">
          {/* Photo */}
          <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-83">
            <div className="bg-[#1A2B4A] border border-[#4ECDC4]/30 rounded-2xl overflow-hidden aspect-[4/3] flex items-end justify-center relative" data-id="element-84">
              <img src="https://cdn.magicpatterns.com/uploads/vhoFfkrow7txhCk3BuwASR/Jake__Peter.png" alt="Jake and Peter — Founders of The Hire Advantage" className="w-full h-full object-cover object-top" data-id="element-85" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A2B4A] to-transparent px-5 py-4" data-id="element-86">
                <p className="text-white font-bold text-sm" data-id="element-87">
                  Jake & Peter — Founders
                </p>
                <p className="text-[#4ECDC4] text-xs font-semibold uppercase tracking-wider" data-id="element-88">
                  20+ Years in D2D
                </p>
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <motion.div custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-89">
            <blockquote className="text-white/80 text-lg leading-relaxed mb-6" data-id="element-90">
              "We're not a recruiting agency that decided to sell into solar and
              roofing. We're door-to-door operators who've lived in the field,
              ran the meetings, knocked the doors, and built the teams from the
              inside for{' '}
              <span className="text-[#4ECDC4] font-semibold" data-id="element-91">20+ years</span>.
            </blockquote>
            <p className="text-white/80 text-lg leading-relaxed mb-8" data-id="element-92">
              This system was battle-tested in a company just like yours — 4,000
              reps, $150M in production, 90% from cold online hires. Now we
              install it for others."
            </p>
            <div className="grid grid-cols-3 gap-3 sm:flex sm:flex-wrap sm:gap-3" data-id="element-93">
              <div className="bg-white/10 border border-[#4ECDC4]/20 rounded-xl px-3 py-3 sm:px-4 sm:py-2 text-center" data-id="element-94">
                <p className="text-xl sm:text-2xl font-black text-[#4ECDC4]" data-id="element-95">
                  4,000+
                </p>
                <p className="text-xs text-white/50 leading-tight" data-id="element-96">
                  Reps Hired
                </p>
              </div>
              <div className="bg-white/10 border border-[#4ECDC4]/20 rounded-xl px-3 py-3 sm:px-4 sm:py-2 text-center" data-id="element-97">
                <p className="text-xl sm:text-2xl font-black text-[#4ECDC4]" data-id="element-98">
                  $150M+
                </p>
                <p className="text-xs text-white/50 leading-tight" data-id="element-99">
                  Production
                </p>
              </div>
              <div className="bg-white/10 border border-[#4ECDC4]/20 rounded-xl px-3 py-3 sm:px-4 sm:py-2 text-center" data-id="element-100">
                <p className="text-xl sm:text-2xl font-black text-[#4ECDC4]" data-id="element-101">
                  90%
                </p>
                <p className="text-xs text-white/50 leading-tight" data-id="element-102">
                  Cold Online Hires
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trust logos */}
        <motion.div custom={3} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="mb-14" data-id="element-103">
          <p className="text-center text-white/40 text-xs uppercase tracking-widest font-semibold mb-6" data-id="element-104">
            Trusted by 30+ D2D Companies
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4" data-id="element-105">
            {logos.map(logo => <div key={logo.alt} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-3 w-28 h-14 flex items-center justify-center" data-id="element-106">
                <img src={logo.src} alt={logo.alt} className="max-h-8 max-w-full object-contain opacity-70" data-id="element-107" />
              </div>)}
          </div>
        </motion.div>

        <motion.div custom={4} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex justify-center" data-id="element-108">
          <CTAButton data-id="element-109" />
        </motion.div>
      </div>
    </section>;
}